import FeatherIcon from "feather-icons-react";
import React, { FC, useMemo, useState } from "react";
import { CsobFormMetaDataDto, QuestionnaireType } from "@green0meter/api-client";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import ExportFormButton from "../ExportFormButton";

import { IconButton, Stack, Tooltip } from "@components/base";
import { useUserStore } from "@store/user";
import { useGetAssignedCompanies, useRemoveCsobForm } from "@api";
import { ConfirmationModal } from "@components/common";

interface Actions {
  form: CsobFormMetaDataDto;
}

const Actions: FC<Actions> = ({ form }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { assignedCompanies } = useGetAssignedCompanies();
  const { user } = useUserStore();
  const { removeForm, removeFormEnabled, isPendingRemoveForm } =
    useRemoveCsobForm(user);

  const [deleteModalOpen, setDeleteModalOpen] = useState<string>();

  const companyName = useMemo(() => {
    return (
      (assignedCompanies &&
        assignedCompanies.find((company) => company.id === user?.activeCompanyId)
          ?.name) ||
      "N/A"
    );
  }, [assignedCompanies, user]);

  return (
    <>
      <Stack direction="row" spacing={1}>
        <Tooltip arrow title={t("csob.questionnaires.edit")} placement="top">
          <IconButton
            size="small"
            onClick={() => navigate(`/csob/form/${form.formId}`)}
            color="primary"
          >
            <FeatherIcon icon="edit-3" size={16} />
          </IconButton>
        </Tooltip>

        <ExportFormButton form={form} companyName={companyName} />

        {form.questionnaireType !== QuestionnaireType.Client &&
          removeFormEnabled && (
            <Tooltip arrow title={t("csob.questionnaires.delete")} placement="top">
              <IconButton
                size="small"
                onClick={() => {
                  setDeleteModalOpen(form.formId);
                }}
                color="error"
                disabled={isPendingRemoveForm}
              >
                <FeatherIcon icon="trash-2" size={16} />
              </IconButton>
            </Tooltip>
          )}
      </Stack>

      <ConfirmationModal
        open={!!deleteModalOpen}
        onClose={() => setDeleteModalOpen(undefined)}
        onConfirm={() => {
          if (deleteModalOpen) {
            removeForm(deleteModalOpen);
          }
          setDeleteModalOpen(undefined);
        }}
        title={t("csob.questionnaires.confirmDelete")}
        description={t("csob.questionnaires.confirmDeleteDescription")}
      />
    </>
  );
};

export default Actions;
